import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { getAuthHeader } from "../../utils/auth";
import { isEnglishOperatorMode } from "../../utils/operatorMode";

const API_URL = window.location.hostname === "localhost"
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const PUBLIC_BOOKING_DISPLAY_TIMEZONE = "Asia/Tokyo";
const DEFAULT_ENGLISH_HOST_TIMEZONE = "America/Los_Angeles";

export const providerCardsJa = [
  { id: "google_calendar", label: "Google Calendar 連携" },
  { id: "google_meet", label: "Google Calendar イベントに Meet リンクを付与" },
  { id: "zoom", label: "Zoom 連携" }
];

export const providerCardsEn = [
  { id: "google_calendar", label: "Google Calendar connection" },
  { id: "google_meet", label: "Add Google Meet links to Calendar events" },
  { id: "zoom", label: "Zoom connection" }
];

export function canManageBookingProviderConnection({ providerId, selectedOwnerUserId, user }) {
  const requesterUserId = Number(user?.id);
  const ownerUserId = Number(selectedOwnerUserId);
  if (!Number.isSafeInteger(requesterUserId) || requesterUserId <= 0
    || !Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) return false;
  if (requesterUserId === ownerUserId) return true;
  return providerId === "zoom" && user?.role === "admin";
}

export const meetingPreferenceOptionsJa = [
  { value: "auto", label: "自動判定", description: "Google Calendar に予定を作成し、必要に応じて Google Meet リンクを付与します。" },
  { value: "google_meet", label: "Google Calendar イベントに Meet リンクを付与", description: "Google Calendar に予定を作成し、必要に応じて Google Meet リンクを付与します。" },
  { value: "zoom", label: "Zoom", description: "担当ユーザーの Zoom アカウントでミーティング URL を発行します。" },
  { value: "none", label: "会議 URL なし", description: "予約だけ確定し、会議 URL は発行しません。" }
];

export const meetingPreferenceOptionsEn = [
  { value: "auto", label: "Auto", description: "Create a Google Calendar event and add a Google Meet link when needed." },
  { value: "google_meet", label: "Google Meet", description: "Create a Google Calendar event and attach a Google Meet link." },
  { value: "zoom", label: "Zoom", description: "Create a meeting URL with the host user's connected Zoom account." },
  { value: "none", label: "No meeting URL", description: "Confirm the booking without issuing a meeting URL." }
];

const dayLabelsJa = ["日", "月", "火", "水", "木", "金", "土"];
const dayLabelsEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const hostTimezoneOptions = [
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Phoenix", label: "Arizona Time (Phoenix)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (Honolulu)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time" }
];

const bookingCopy = {
  ja: {
    close: "閉じる",
    unset: "未設定",
    notIssued: "未発行",
    userSettingsTitle: "担当ユーザー設定",
    userSettingsDescription: "連携設定と空き枠設定はここにまとめます。",
    targetUser: "設定対象ユーザー",
    editingUser: (name) => <>いまは <span className="font-bold">{name || "未選択"}</span> の設定を編集しています。ここで接続した Google Calendar / Meet / Zoom と空き枠ルールが、予約リンクから使われます。</>,
    googleWarning: "Google Calendar 未接続だと、予約は成立しても Google カレンダーには反映されません。",
    zoomWarning: "Zoom 未接続のため、Zoom を指定した予約リンクは公開できません。担当ユーザー本人またはシステム管理者が Zoom を接続してください。",
    providerOwnerOnly: "Google OAuth 連携は対象ユーザー本人のみ操作できます。システム管理者は選択中の担当ユーザーの Zoom 連携を代理操作できます。",
    providerSettings: "連携設定",
    availabilityRules: "空き枠ルール",
    availabilityDescription: "追加、削除、保存までここで完結させます。",
    addRule: "ルール追加",
    day: "曜日",
    start: "開始",
    end: "終了",
    duration: "枠分数",
    deleteRule: "このルールを削除",
    noRules: "まだ営業時間ルールがありません。まず 1 件追加してください。",
    saving: "保存中...",
    saveAvailability: "空き枠設定を保存",
    workspaceTitle: "日程調整",
    workspaceDescription: "予約リンクを主役にして、必要なときだけ予約ログとユーザー設定を開く構成に寄せています。",
    userSettings: "ユーザー設定",
    newLinkPanelTitle: "新規予約リンク",
    newLinkPanelSubtitle: "誰の予定を押さえるか、どの会議アプリを使うかを先に決めます。",
    newLinkButton: "新規予約リンク作成",
    activeLinks: "公開中リンク",
    weekAppointments: "今週の予約",
    activeConnections: "有効な連携",
    linkListTitle: "予約リンク一覧",
    linkListDescription: "長くなりすぎないよう、予約履歴は必要なリンクだけ開くログ形式にしています。",
    linkName: "予約リンク名",
    ownerUser: "担当ユーザー",
    ownerHint: "リンク作成時に、誰の予定を押さえるかをここで選びます。",
    meetingPreference: "会議 URL の発行方法",
    description: "説明",
    createLink: "予約リンクを作成",
    saveLink: "予約リンクを保存",
    savingLink: "保存中...",
    minimumNoticeHours: "最低受付時間",
    minimumNoticeHint: "現在時刻から何時間後以降の枠を表示するかを設定します。0なら直近枠も受付します。",
    maxDaysInFuture: "提示期間",
    maxDaysHint: "今日から何日先まで予約候補を表示するかを設定します。",
    hoursUnit: "時間",
    daysUnit: "日",
    createdManually: "手動作成",
    untitledLink: "無題リンク",
    ownerLabel: "担当",
    meetingUrl: "会議 URL",
    publicUrl: "公開 URL",
    openBookingPage: "予約ページを開く",
    draftMessage: "下書きです。公開後に利用できます。",
    publishing: "更新中...",
    publish: "公開する",
    unpublish: "非公開にする",
    closeLog: "ログを閉じる",
    viewLog: (count) => `予約ログを見る (${count})`,
    noAppointments: "この予約リンクに紐づく予約はまだありません。",
    companyUnset: "会社名未設定",
    releaseSlot: "枠を解放",
    releasing: "解放中...",
    noLinks: "まだ予約リンクはありません。",
    creatingLink: "予約リンクを作成中...",
    appointmentDetail: "Appointment Detail",
    abuseSignals: "いたずら検知",
    riskScore: "リスクスコア",
    riskFlags: "検知理由",
    noRiskFlags: "強い検知理由はありません",
    trackingId: "Tracking ID",
    visitorId: "Visitor ID",
    ipAddress: "IPアドレス",
    referrer: "参照元",
    landingUrl: "予約URL",
    userAgent: "User Agent",
    status: "状態",
    dateTime: "日時",
    crmSync: "CRM同期",
    actions: "操作",
    openManagePage: "公開管理ページを開く",
    openReschedulePage: "公開日程変更ページを開く",
    cancelAndRelease: "予約をキャンセルして枠を解放",
    resyncMeeting: "会議URLの発行を再試行",
    resyncingMeeting: "再試行中...",
    resyncMeetingHelp: "予約枠は維持したまま、外部会議URLの発行と予約確定だけを再試行します。",
    resyncMeetingSuccess: "会議URLを発行し、予約を確定しました。",
    resyncMeetingPending: "会議URLをまだ発行できませんでした。連携状態を確認してから再度お試しください。",
    resyncMeetingReconciliation: "Zoom側の作成結果を安全に照合する必要があります。重複発行を防ぐため再試行せず、サポートへご連絡ください。",
    resyncMeetingInProgress: "Zoom側の作成結果を確認中です。この画面での再試行は不要です。",
    retryProviderCleanup: "外部会議の削除を再試行",
    retryingProviderCleanup: "削除を再試行中...",
    providerCleanupHelp: "SalesBreaker上のキャンセルは完了しています。Zoom等に残った会議の削除だけを再試行します。",
    providerCleanupSuccess: "外部会議の削除を確認しました。",
    createBookingLink: "Create Booking Link",
    bookingLinkDetail: "Booking Link Detail",
    editBookingLink: "予約リンク編集",
    editLinkPanelTitle: "予約リンク編集",
    editLinkPanelSubtitle: "タイトル、説明、受付条件を変更できます。既存予約には影響しません。",
    hostTimezone: "管理側タイムゾーン",
    hostTimezoneHint: "",
    jstPreview: "",
    publicTimezoneNote: "",
  },
  en: {
    close: "Close",
    unset: "Not set",
    notIssued: "Not issued",
    userSettingsTitle: "User Settings",
    userSettingsDescription: "Manage calendar connections and availability rules here.",
    targetUser: "Target user",
    editingUser: (name) => <>You are editing settings for <span className="font-bold">{name || "No user selected"}</span>. Calendar connections and availability rules are used by booking links.</>,
    googleWarning: "Without Google Calendar connection, bookings can be accepted but will not be written to Google Calendar.",
    zoomWarning: "Zoom is not connected, so booking links that require Zoom cannot be published. The host or a system administrator must connect Zoom.",
    providerOwnerOnly: "Only the selected host can manage Google OAuth. System administrators may manage Zoom for the selected host.",
    providerSettings: "Calendar Connections",
    availabilityRules: "Availability Rules",
    availabilityDescription: "Set host-side working hours. Public booking pages are shown in Japan time.",
    addRule: "Add Rule",
    day: "Day",
    start: "Start",
    end: "End",
    duration: "Duration",
    deleteRule: "Delete this rule",
    noRules: "No availability rule yet. Add at least one rule.",
    saving: "Saving...",
    saveAvailability: "Save availability",
    workspaceTitle: "Booking",
    workspaceDescription: "Create booking links, review bookings, and manage user-level calendar settings.",
    userSettings: "User Settings",
    newLinkPanelTitle: "New booking link",
    newLinkPanelSubtitle: "Choose the host user and meeting URL behavior before creating the link.",
    newLinkButton: "Create booking link",
    activeLinks: "Active Links",
    weekAppointments: "Confirmed Bookings",
    activeConnections: "Active Connections",
    linkListTitle: "Booking Links",
    linkListDescription: "Open logs only when you need to review bookings for a specific link.",
    linkName: "Booking link name",
    ownerUser: "Host user",
    ownerHint: "Choose whose calendar and availability this link should use.",
    meetingPreference: "Meeting URL behavior",
    description: "Description",
    createLink: "Create booking link",
    saveLink: "Save booking link",
    savingLink: "Saving...",
    minimumNoticeHours: "Minimum notice",
    minimumNoticeHint: "Hide slots that start before the specified number of hours from now. Use 0 to allow immediate bookings.",
    maxDaysInFuture: "Display window",
    maxDaysHint: "Choose how many days from today should be shown on the public booking page.",
    hoursUnit: "hours",
    daysUnit: "days",
    createdManually: "Manual link",
    untitledLink: "Untitled link",
    ownerLabel: "Host",
    meetingUrl: "Meeting URL",
    publicUrl: "Public URL",
    openBookingPage: "Open booking page",
    draftMessage: "Draft. Publish it before sharing.",
    publishing: "Updating...",
    publish: "Publish",
    unpublish: "Unpublish",
    closeLog: "Close log",
    viewLog: (count) => `View bookings (${count})`,
    noAppointments: "No bookings are linked to this booking link yet.",
    companyUnset: "Company not set",
    releaseSlot: "Release slot",
    releasing: "Releasing...",
    noLinks: "No booking links yet.",
    creatingLink: "Creating booking link...",
    appointmentDetail: "Appointment Detail",
    abuseSignals: "Abuse Signals",
    riskScore: "Risk score",
    riskFlags: "Signals",
    noRiskFlags: "No strong signal recorded",
    trackingId: "Tracking ID",
    visitorId: "Visitor ID",
    ipAddress: "IP address",
    referrer: "Referrer",
    landingUrl: "Booking URL",
    userAgent: "User Agent",
    status: "Status",
    dateTime: "Date & time",
    crmSync: "CRM sync",
    actions: "Actions",
    openManagePage: "Open public manage page",
    openReschedulePage: "Open public reschedule page",
    cancelAndRelease: "Cancel booking and release slot",
    resyncMeeting: "Retry meeting URL sync",
    resyncingMeeting: "Retrying...",
    resyncMeetingHelp: "Keep the slot reserved and retry only the external meeting URL and confirmation sync.",
    resyncMeetingSuccess: "The meeting URL was issued and the booking was confirmed.",
    resyncMeetingPending: "The meeting URL could not be issued yet. Check the connection and try again.",
    resyncMeetingReconciliation: "The Zoom result requires reconciliation. To avoid a duplicate meeting, do not retry and contact support.",
    resyncMeetingInProgress: "The Zoom result is still being checked. No retry is needed on this screen.",
    retryProviderCleanup: "Retry external meeting cleanup",
    retryingProviderCleanup: "Retrying cleanup...",
    providerCleanupHelp: "The SalesBreaker cancellation is complete. Retry only deletion of the external meeting.",
    providerCleanupSuccess: "External meeting cleanup was confirmed.",
    createBookingLink: "Create Booking Link",
    bookingLinkDetail: "Booking Link Detail",
    editBookingLink: "Edit Booking Link",
    editLinkPanelTitle: "Edit booking link",
    editLinkPanelSubtitle: "Update the title, description, and booking conditions. Existing appointments will not be changed.",
    hostTimezone: "Host timezone",
    hostTimezoneHint: "Japan GTM booking pages still display slots in Japan time for invitees.",
    jstPreview: "Japan preview",
    publicTimezoneNote: "Invitees will see available slots in Japan Standard Time.",
  }
};

function StatusPill({ value }) {
  const tone =
    value === "active" || value === "confirmed" || value === "completed"
      ? "bg-emerald-100 text-emerald-700"
      : value === "draft" || value === "pending"
        ? "bg-amber-100 text-amber-700"
        : value === "cancelled" || value === "disabled"
          ? "bg-rose-100 text-rose-700"
          : "bg-gray-100 text-gray-600";

  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${tone}`}>{value}</span>;
}

function SectionCard({ title, description, actionLabel, onAction, children }) {
  return (
    <section className="rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="w-full rounded-full bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800 sm:w-auto"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-gray-700">{label}</span>
      {children}
      {hint ? <span className="mt-2 block text-xs leading-5 text-gray-500">{hint}</span> : null}
    </label>
  );
}

function formatDateTime(value) {
  if (!value) return "未設定";
  return new Date(value).toLocaleString("ja-JP");
}

function normalizeTimezone(timezone, fallback = PUBLIC_BOOKING_DISPLAY_TIMEZONE) {
  try {
    const candidate = String(timezone || "").trim();
    if (!candidate) return fallback;
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(new Date());
    return candidate;
  } catch (error) {
    return fallback;
  }
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getZonedParts(value, timezone) {
  const date = value instanceof Date ? value : new Date(value);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: normalizeTimezone(timezone),
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  return formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});
}

function getTimezoneOffsetMilliseconds(value, timezone) {
  const date = value instanceof Date ? value : new Date(value);
  const parts = getZonedParts(date, timezone);
  const zonedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return zonedAsUtc - date.getTime();
}

function zonedDateTimeToDate(dateValue, timeValue, timezone) {
  const [year, month, day] = String(dateValue || "").split("-").map((part) => Number.parseInt(part, 10));
  const [hours, minutes, seconds] = String(timeValue || "00:00:00").slice(0, 8).split(":").map((part) => Number.parseInt(part, 10));
  if (![year, month, day, hours, minutes].every(Number.isInteger)) return null;

  const utcGuess = Date.UTC(year, month - 1, day, hours, minutes, Number.isInteger(seconds) ? seconds : 0);
  const initial = new Date(utcGuess - getTimezoneOffsetMilliseconds(new Date(utcGuess), timezone));
  return new Date(utcGuess - getTimezoneOffsetMilliseconds(initial, timezone));
}

function getNextDateForWeekday(dayOfWeek) {
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const deltaDays = (Number(dayOfWeek) - base.getDay() + 7) % 7;
  base.setDate(base.getDate() + deltaDays);
  return toDateInputValue(base);
}

function formatJstPreview(rule, hostTimezone) {
  const dateValue = getNextDateForWeekday(rule.day_of_week);
  const startDate = zonedDateTimeToDate(dateValue, rule.start_time || "10:00:00", hostTimezone);
  const endDate = zonedDateTimeToDate(dateValue, rule.end_time || "18:00:00", hostTimezone);
  if (!startDate || !endDate) return "";

  const startLabel = startDate.toLocaleString("en-US", {
    timeZone: PUBLIC_BOOKING_DISPLAY_TIMEZONE,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
  const endLabel = endDate.toLocaleTimeString("en-US", {
    timeZone: PUBLIC_BOOKING_DISPLAY_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit"
  });

  return `${startLabel} - ${endLabel} JST`;
}

function buildPublicBookingUrl(publicId) {
  const baseUrl = window.location.hostname === "localhost"
    ? "http://localhost:3000/app"
    : `${window.location.origin}/app`;
  return `${baseUrl}/public/booking/${publicId}`;
}

function buildPublicManageUrl(linkPublicId, appointmentPublicId) {
  return `${buildPublicBookingUrl(linkPublicId)}/manage/${appointmentPublicId}`;
}

function buildPublicRescheduleUrl(linkPublicId, appointmentPublicId) {
  return `${buildPublicBookingUrl(linkPublicId)}/reschedule/${appointmentPublicId}`;
}

function needsGoogleCalendarWarning(meetingPreference) {
  return meetingPreference === "auto" || meetingPreference === "google_meet";
}

function needsZoomWarning(meetingPreference) {
  return meetingPreference === "zoom";
}

export function getMeetingConnectionWarning(meetingPreference, connections, copy) {
  if (needsZoomWarning(meetingPreference) && !connections.zoomConnected) {
    return copy.zoomWarning;
  }
  if (needsGoogleCalendarWarning(meetingPreference) && !connections.googleCalendarConnected) {
    return copy.googleWarning;
  }
  return "";
}

function canCancelAppointment(appointment) {
  return ["confirmed", "pending"].includes(appointment?.status);
}

export function canResyncAppointment(appointment) {
  return appointment?.provider_reconciliation_required !== true
    && appointment?.provider_sync_in_progress !== true
    && ["pending", "failed", "error", "sync_error"].includes(
    String(appointment?.status || "").trim().toLowerCase()
  );
}

function parseJsonField(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function getRiskScore(appointment) {
  const score = Number(appointment?.risk_score || 0);
  return Number.isFinite(score) ? score : 0;
}

function getRiskLevel(appointment) {
  return appointment?.risk_level || (getRiskScore(appointment) >= 70 ? "high" : getRiskScore(appointment) >= 35 ? "medium" : "low");
}

function getRiskTone(appointment) {
  const level = getRiskLevel(appointment);
  if (level === "high") return "border-rose-200 bg-rose-50 text-rose-700";
  if (level === "medium") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function getRiskFlags(appointment) {
  const parsed = parseJsonField(appointment?.risk_flags);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.flags)) return parsed.flags;
  return [];
}

function formatRiskFlag(flag) {
  if (!flag) return "";
  if (typeof flag === "string") return flag;
  const points = Number.isFinite(Number(flag.points)) ? ` +${flag.points}` : "";
  return `${flag.code || flag.message || "signal"}${points}`;
}

export function ProviderConnectionCard({ provider, label, isLoading, onConnect, onDisconnect, isEnglishMode, canManage }) {
  const isConnected = provider?.status === "active";
  const isReady = Boolean(provider?.connection_ready);

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <p className="text-sm font-bold text-gray-900">{label}</p>
        <StatusPill value={provider?.status || "not_connected"} />
      </div>
      <p className="mt-2 text-sm text-gray-500">
        {isReady
          ? isEnglishMode
            ? `${provider.external_account_email || "Connected account"} is ready.`
            : `${provider.external_account_email || "接続済みアカウント"} で利用できます。`
          : isConnected
            ? isEnglishMode
              ? "Connection exists. Complete OAuth to enable calendar sync."
              : "接続レコードはあります。OAuth 完了で自動連携が有効になります。"
            : isEnglishMode
              ? "Not connected."
              : "未接続です。"}
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onConnect}
          disabled={isLoading || !canManage}
          className="w-full rounded-full bg-gray-900 px-4 py-2 text-xs font-bold text-white transition hover:bg-gray-800 disabled:bg-gray-400 sm:w-auto"
        >
          {isLoading ? (isEnglishMode ? "Updating..." : "更新中...") : isConnected ? (isEnglishMode ? "Reconnect" : "接続更新") : (isEnglishMode ? "Connect" : "接続")}
        </button>
        {isConnected ? (
          <button
            type="button"
            onClick={onDisconnect}
            disabled={isLoading || !canManage}
            className="w-full rounded-full border border-gray-300 px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-white disabled:opacity-60 sm:w-auto"
          >
            {isEnglishMode ? "Disconnect" : "切断"}
          </button>
        ) : null}
      </div>
      {!canManage ? (
        <p className="mt-3 text-xs font-semibold leading-5 text-amber-700">
          {isEnglishMode
            ? "The host must sign in and manage this connection themselves."
            : "連携の接続・切断は、担当ユーザー本人がログインして操作してください。"}
        </p>
      ) : null}
    </div>
  );
}

function AppointmentDetailDrawer({
  appointment,
  onClose,
  onCancelAppointment,
  onResyncAppointment,
  onRetryProviderCleanup,
  isCancellingAppointment,
  isResyncingAppointment,
  resyncFeedback,
  copy
}) {
  if (!appointment) return null;

  const riskScore = getRiskScore(appointment);
  const riskLevel = getRiskLevel(appointment);
  const riskFlags = getRiskFlags(appointment);

  return (
    <div className="fixed inset-0 z-[110] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-4 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-600">{copy.appointmentDetail}</p>
              <h3 className="mt-2 text-xl font-bold text-gray-900 sm:text-2xl">{appointment.invitee_name}</h3>
              <p className="mt-2 text-sm text-gray-500">{appointment.invitee_company || copy.companyUnset}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              aria-label={copy.close}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.status}</p>
              <div className="mt-2">
                <StatusPill value={appointment.status} />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.dateTime}</p>
              <p className="mt-2 text-sm font-bold text-gray-900">{formatDateTime(appointment.started_at)}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.meetingUrl}</p>
            <p className="mt-2 break-all text-sm text-gray-600">
              {appointment.status === "confirmed" ? (appointment.meeting_url || copy.notIssued) : copy.notIssued}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.crmSync}</p>
            <div className="mt-3 flex gap-3">
              <StatusPill value={appointment.synced_to_deal_at ? "completed" : "not_started"} />
              <StatusPill value={appointment.synced_to_marketing_lead_at ? "completed" : "not_started"} />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.abuseSignals}</p>
              <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-black ${getRiskTone(appointment)}`}>
                {copy.riskScore}: {riskScore} / {riskLevel}
              </span>
            </div>
            <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.ipAddress}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.ip_address || copy.unset}</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.trackingId}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.tracking_id || copy.unset}</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.visitorId}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.visitor_cookie_id || copy.unset}</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.referrer}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.referer || copy.unset}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.landingUrl}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.landing_url || copy.unset}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.userAgent}</p>
                <p className="mt-1 break-all text-gray-700">{appointment.user_agent || copy.unset}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">{copy.riskFlags}</p>
              {riskFlags.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {riskFlags.map((flag, index) => (
                    <span key={`${formatRiskFlag(flag)}-${index}`} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">
                      {formatRiskFlag(flag)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">{copy.noRiskFlags}</p>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.actions}</p>
            <div className="mt-3 flex flex-col gap-3">
              {appointment.provider_reconciliation_required ? (
                <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold leading-6 text-red-800">
                  {copy.resyncMeetingReconciliation}
                </div>
              ) : appointment.provider_sync_in_progress ? (
                <div role="status" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-900">
                  {copy.resyncMeetingInProgress}
                </div>
              ) : canResyncAppointment(appointment) ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm leading-6 text-amber-900">{copy.resyncMeetingHelp}</p>
                  <button
                    type="button"
                    onClick={() => onResyncAppointment(appointment)}
                    disabled={isResyncingAppointment || isCancellingAppointment}
                    className="mt-3 w-full rounded-2xl bg-amber-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-amber-300"
                  >
                    {isResyncingAppointment ? copy.resyncingMeeting : copy.resyncMeeting}
                  </button>
                </div>
              ) : null}
              {appointment.provider_cleanup_required ? (
                <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                  <p className="text-sm leading-6 text-orange-900">{copy.providerCleanupHelp}</p>
                  <button
                    type="button"
                    onClick={() => onRetryProviderCleanup(appointment)}
                    disabled={isCancellingAppointment || isResyncingAppointment}
                    className="mt-3 w-full rounded-2xl bg-orange-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-orange-300"
                  >
                    {isCancellingAppointment ? copy.retryingProviderCleanup : copy.retryProviderCleanup}
                  </button>
                </div>
              ) : null}
              {resyncFeedback?.message ? (
                <div
                  role={resyncFeedback.type === "error" ? "alert" : "status"}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                    resyncFeedback.type === "error"
                      ? "border-red-200 bg-red-50 text-red-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {resyncFeedback.message}
                </div>
              ) : null}
              {appointment.booking_link_public_id ? (
                <>
                  <a
                    href={buildPublicManageUrl(appointment.booking_link_public_id, appointment.public_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                  >
                    {copy.openManagePage}
                  </a>
                  {appointment.status === "confirmed" ? (
                    <a
                      href={buildPublicRescheduleUrl(appointment.booking_link_public_id, appointment.public_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-2xl border border-blue-200 px-4 py-3 text-center text-sm font-bold text-blue-700 transition hover:bg-blue-50"
                    >
                      {copy.openReschedulePage}
                    </a>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                onClick={() => onCancelAppointment(appointment)}
                disabled={isCancellingAppointment || isResyncingAppointment || !canCancelAppointment(appointment)}
                className="rounded-2xl border border-rose-200 px-4 py-3 text-sm font-bold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
              >
                {isCancellingAppointment ? copy.releasing : copy.cancelAndRelease}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function BookingLinkDrawer({
  panel,
  users,
  createDraft,
  connectionWarning,
  meetingPreferenceOptions,
  copy,
  isCreatingLink,
  isSavingLink,
  onDraftChange,
  onCreateLink,
  onSaveLink,
  onClose
}) {
  if (!panel) return null;

  const isNewLink = panel.kind === "new-link";
  const isEditingLink = panel.kind === "edit-link";
  const isLinkForm = isNewLink || isEditingLink;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-4 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">
                {isNewLink ? copy.createBookingLink : isEditingLink ? copy.editBookingLink : copy.bookingLinkDetail}
              </p>
              <h3 className="mt-2 text-xl font-bold text-gray-900 sm:text-2xl">{panel.title}</h3>
              <p className="mt-2 text-sm text-gray-500">{panel.subtitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              aria-label={copy.close}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLinkForm ? (
            <div className="space-y-5">
              <Field label={copy.linkName}>
                <input
                  value={createDraft.title}
                  onChange={(event) => onDraftChange("title", event.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                />
              </Field>
              <Field label={copy.ownerUser} hint={copy.ownerHint}>
                <select
                  value={createDraft.owner_user_id}
                  onChange={(event) => onDraftChange("owner_user_id", event.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                >
                  {users.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={copy.meetingPreference}>
                <select
                  value={createDraft.meeting_app_preference}
                  onChange={(event) => onDraftChange("meeting_app_preference", event.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                >
                  {meetingPreferenceOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <p className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-700">
                {meetingPreferenceOptions.find((option) => option.value === createDraft.meeting_app_preference)?.description}
              </p>
              {connectionWarning ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                  {connectionWarning}
                </div>
              ) : null}
              <Field label={copy.description}>
                <textarea
                  rows="4"
                  value={createDraft.description}
                  onChange={(event) => onDraftChange("description", event.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                  />
              </Field>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label={`${copy.minimumNoticeHours} (${copy.hoursUnit})`} hint={copy.minimumNoticeHint}>
                  <input
                    type="number"
                    min="0"
                    max="720"
                    step="1"
                    value={createDraft.booking_notice_hours}
                    onChange={(event) => onDraftChange("booking_notice_hours", Number(event.target.value))}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                  />
                </Field>
                <Field label={`${copy.maxDaysInFuture} (${copy.daysUnit})`} hint={copy.maxDaysHint}>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    step="1"
                    value={createDraft.max_days_in_future}
                    onChange={(event) => onDraftChange("max_days_in_future", Number(event.target.value))}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                  />
                </Field>
              </div>
              <button
                type="button"
                onClick={isEditingLink ? onSaveLink : onCreateLink}
                disabled={isCreatingLink || isSavingLink}
                className="w-full rounded-2xl bg-gray-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-gray-800 disabled:bg-gray-400"
              >
                {isEditingLink
                  ? (isSavingLink ? copy.savingLink : copy.saveLink)
                  : (isCreatingLink ? copy.creatingLink : copy.createLink)}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.ownerUser}</p>
                  <p className="mt-2 text-sm font-bold text-gray-900">{panel.ownerLabel}</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.meetingPreference}</p>
                  <p className="mt-2 text-sm font-bold text-gray-900">{panel.meetingPreferenceLabel}</p>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.publicUrl}</p>
                <a href={buildPublicBookingUrl(panel.public_id)} target="_blank" rel="noreferrer" className="mt-2 block break-all text-sm font-bold text-blue-600 hover:text-blue-700">
                  {buildPublicBookingUrl(panel.public_id)}
                </a>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.description}</p>
                <p className="mt-2 text-sm leading-6 text-gray-600">{panel.description || copy.unset}</p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function UserSettingsModal({
  open,
  users,
  selectedOwnerUserId,
  onOwnerChange,
  providers,
  providerCards,
  providerLoadingId,
  onProviderAction,
  canManageProviderConnection,
  availability,
  onTimezoneChange,
  onRuleChange,
  onAddRule,
  onRemoveRule,
  onSaveAvailability,
  isSavingAvailability,
  availabilityFeedback,
  isEnglishMode,
  dayLabels,
  copy
}) {
  if (!open) return null;

  const selectedOwner = users.find((item) => String(item.id) === String(selectedOwnerUserId));
  const hostTimezone = normalizeTimezone(availability.timezone || (isEnglishMode ? DEFAULT_ENGLISH_HOST_TIMEZONE : PUBLIC_BOOKING_DISPLAY_TIMEZONE));

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onOwnerChange.bind(null, "__close__")} />
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[24px] bg-white shadow-2xl sm:rounded-[32px]">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-5 sm:px-6">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">User Settings</p>
            <h3 className="mt-2 text-xl font-bold text-gray-900 sm:text-2xl">{copy.userSettingsTitle}</h3>
            <p className="mt-2 text-sm text-gray-500">{copy.userSettingsDescription}</p>
          </div>
          <button
            type="button"
            onClick={() => onOwnerChange("__close__")}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label={copy.close}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-6 px-4 py-5 sm:px-6 sm:py-6">
          <Field label={copy.targetUser}>
            <select
              value={selectedOwnerUserId}
              onChange={(event) => onOwnerChange(event.target.value)}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
            >
              {users.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </Field>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-700">
            {copy.editingUser(selectedOwner?.name)}
          </div>
          {providerCards.some((providerCard) => !canManageProviderConnection(providerCard.id)) ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
              {copy.providerOwnerOnly}
            </div>
          ) : null}
          {!providers.find((provider) => provider.provider === "google_calendar")?.connection_ready ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
              {copy.googleWarning}
            </div>
          ) : null}

          <div className="grid gap-6 xl:grid-cols-[1fr_1.2fr]">
            <div className="space-y-3">
              <h4 className="text-lg font-bold text-gray-900">{copy.providerSettings}</h4>
              {providerCards.map((providerCard) => {
                const provider = providers.find((item) => item.provider === providerCard.id);
                return (
                  <ProviderConnectionCard
                    key={providerCard.id}
                    provider={provider}
                    label={providerCard.label}
                    isLoading={providerLoadingId === providerCard.id}
                    onConnect={() => onProviderAction(providerCard.id, "connect")}
                    onDisconnect={() => onProviderAction(providerCard.id, "disconnect")}
                    isEnglishMode={isEnglishMode}
                    canManage={canManageProviderConnection(providerCard.id)}
                  />
                );
              })}
            </div>

            <div className="space-y-4">
              {isEnglishMode ? (
                <Field label={copy.hostTimezone} hint={copy.hostTimezoneHint}>
                  <select
                    value={hostTimezone}
                    onChange={(event) => onTimezoneChange(event.target.value)}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500"
                  >
                    {hostTimezoneOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <span className="mt-2 block text-xs font-semibold text-blue-600">{copy.publicTimezoneNote}</span>
                </Field>
              ) : null}

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-lg font-bold text-gray-900">{copy.availabilityRules}</h4>
                  <p className="mt-1 text-sm text-gray-500">{copy.availabilityDescription}</p>
                </div>
                <button
                  type="button"
                  onClick={onAddRule}
                  className="w-full rounded-full border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50 sm:w-auto"
                >
                  {copy.addRule}
                </button>
              </div>

              {(availability.rules || []).map((rule, index) => (
                <div key={`${rule.id || "new"}-${index}`} className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="grid gap-3 md:grid-cols-4">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-gray-400">{copy.day}</span>
                      <select
                        value={rule.day_of_week}
                        onChange={(event) => onRuleChange(index, "day_of_week", Number(event.target.value))}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2"
                      >
                        {dayLabels.map((label, dayIndex) => (
                          <option key={label} value={dayIndex}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-gray-400">{copy.start}</span>
                      <input
                        type="time"
                        value={String(rule.start_time || "10:00:00").slice(0, 5)}
                        onChange={(event) => onRuleChange(index, "start_time", `${event.target.value}:00`)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-gray-400">{copy.end}</span>
                      <input
                        type="time"
                        value={String(rule.end_time || "18:00:00").slice(0, 5)}
                        onChange={(event) => onRuleChange(index, "end_time", `${event.target.value}:00`)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-gray-400">{copy.duration}</span>
                      <input
                        type="number"
                        min="15"
                        step="15"
                        value={rule.slot_duration_minutes || 30}
                        onChange={(event) => onRuleChange(index, "slot_duration_minutes", Number(event.target.value))}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2"
                      />
                    </label>
                  </div>
                  {isEnglishMode ? (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold leading-5 text-blue-700">
                      {copy.jstPreview}: {formatJstPreview(rule, hostTimezone)}
                    </div>
                  ) : null}
                  <div className="flex justify-stretch sm:justify-end">
                    <button
                      type="button"
                      onClick={() => onRemoveRule(index)}
                      className="w-full rounded-full border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 transition hover:bg-rose-50 sm:w-auto"
                    >
                      {copy.deleteRule}
                    </button>
                  </div>
                </div>
              ))}

              {(!availability.rules || availability.rules.length === 0) ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  {copy.noRules}
                </div>
              ) : null}

              {availabilityFeedback?.message ? (
                <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                  availabilityFeedback.type === "error"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}>
                  {availabilityFeedback.message}
                </div>
              ) : null}

              <button
                type="button"
                onClick={onSaveAvailability}
                disabled={isSavingAvailability}
                className="w-full rounded-2xl bg-gray-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-gray-800 disabled:bg-gray-400"
              >
                {isSavingAvailability ? copy.saving : copy.saveAvailability}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookingSettingsPage() {
  const { user } = useOutletContext();
  const location = useLocation();
  const isEnglishMode = isEnglishOperatorMode() && !user?.oem_id;
  const copy = isEnglishMode ? bookingCopy.en : bookingCopy.ja;
  const dayLabels = isEnglishMode ? dayLabelsEn : dayLabelsJa;
  const providerCards = isEnglishMode ? providerCardsEn : providerCardsJa;
  const meetingPreferenceOptions = isEnglishMode ? meetingPreferenceOptionsEn : meetingPreferenceOptionsJa;
  const defaultHostTimezone = isEnglishMode ? DEFAULT_ENGLISH_HOST_TIMEZONE : PUBLIC_BOOKING_DISPLAY_TIMEZONE;
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [bookingLinks, setBookingLinks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState({ rules: [], exceptions: [] });
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState("");
  const [openLogLinkId, setOpenLogLinkId] = useState(null);
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isSavingLink, setIsSavingLink] = useState(false);
  const [providerLoadingId, setProviderLoadingId] = useState("");
  const [linkStatusLoadingId, setLinkStatusLoadingId] = useState(null);
  const [appointmentActionLoadingId, setAppointmentActionLoadingId] = useState(null);
  const [appointmentResyncLoadingId, setAppointmentResyncLoadingId] = useState(null);
  const [appointmentResyncFeedback, setAppointmentResyncFeedback] = useState({ appointmentId: null, type: "", message: "" });
  const appointmentResyncInFlightRef = useRef(new Set());
  const oauthCompletionInFlightRef = useRef(false);
  const oauthCompletionCapturedHashRef = useRef("");
  const [oauthCompletionPending, setOauthCompletionPending] = useState(null);
  const [availabilityFeedback, setAvailabilityFeedback] = useState({ type: "", message: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [createDraft, setCreateDraft] = useState({
    title: isEnglishMode ? "Introductory meeting" : "初回相談",
    description: "",
    owner_user_id: "",
    meeting_app_preference: "auto",
    booking_notice_hours: 1,
    max_days_in_future: 30
  });

  const selectedClientId = useMemo(() => {
    if (!user) return null;
    return user.role === "admin" || user.role === "admin_staff"
      ? localStorage.getItem("selectedClientId") || ""
      : user.client_id;
  }, [user]);

  const userNameMap = useMemo(() => new Map(users.map((item) => [Number(item.id), item.name])), [users]);

  const appointmentsByLinkId = useMemo(() => {
    const nextMap = new Map();
    appointments.forEach((appointment) => {
      const linkId = Number(appointment.booking_link_id || 0);
      if (!linkId) return;
      if (!nextMap.has(linkId)) nextMap.set(linkId, []);
      nextMap.get(linkId).push(appointment);
    });
    return nextMap;
  }, [appointments]);

  const activeLinkCount = bookingLinks.filter((link) => link.status === "active").length;
  const activeConnectionCount = providers.filter((provider) => provider.connection_ready).length;
  const thisWeekAppointments = appointments.filter((appointment) => appointment.status === "confirmed").length;
  const googleCalendarProviderStatus = providers.find((provider) => provider.provider === "google_calendar");
  const googleCalendarConnected = Boolean(googleCalendarProviderStatus?.connection_ready);
  const zoomProviderStatus = providers.find((provider) => provider.provider === "zoom");
  const zoomConnected = Boolean(zoomProviderStatus?.connection_ready);
  const providerConnections = useMemo(() => ({
    googleCalendarConnected,
    zoomConnected
  }), [googleCalendarConnected, zoomConnected]);
  const createDraftConnectionWarning = getMeetingConnectionWarning(
    createDraft.meeting_app_preference,
    providerConnections,
    copy
  );
  const canManageProviderConnection = useCallback((providerId) => (
    canManageBookingProviderConnection({ providerId, selectedOwnerUserId, user })
  ), [selectedOwnerUserId, user]);

  const hydrateOwnerState = useCallback((userRows) => {
    const currentUserInClientRows = userRows.find((item) => String(item.id) === String(user?.id));
    const defaultOwnerId = String(currentUserInClientRows?.id || userRows[0]?.id || "");
    setSelectedOwnerUserId((current) => (
      userRows.some((item) => String(item.id) === String(current)) ? current : defaultOwnerId
    ));
    setCreateDraft((current) => ({
      ...current,
      owner_user_id: userRows.some((item) => String(item.id) === String(current.owner_user_id))
        ? current.owner_user_id
        : defaultOwnerId
    }));
  }, [user?.id]);

  const fetchUserScopedSettings = useCallback(async (clientId, ownerUserId) => {
    if (!clientId || !ownerUserId) return;
    const authHeaders = getAuthHeader();
    const [availabilityRes, providersRes] = await Promise.all([
      fetch(`${API_URL}/api/booking/availability?client_id=${encodeURIComponent(clientId)}&owner_user_id=${encodeURIComponent(ownerUserId)}`, { headers: authHeaders }),
      fetch(`${API_URL}/api/booking/providers?client_id=${encodeURIComponent(clientId)}&owner_user_id=${encodeURIComponent(ownerUserId)}`, { headers: authHeaders })
    ]);

    const availabilityJson = await availabilityRes.json();
    const providersJson = await providersRes.json();

    if (!availabilityRes.ok) {
      throw new Error(availabilityJson.error || "空き枠設定の取得に失敗しました。");
    }
    if (!providersRes.ok) {
      throw new Error(providersJson.error || "連携状態の取得に失敗しました。");
    }

    const nextAvailability = availabilityJson.availability || { rules: [], exceptions: [] };
    const nextRules = Array.isArray(nextAvailability.rules) ? nextAvailability.rules : [];
    const nextTimezone = isEnglishMode && nextRules.length === 0
      ? defaultHostTimezone
      : normalizeTimezone(nextAvailability.timezone || defaultHostTimezone, defaultHostTimezone);
    setAvailability({
      ...nextAvailability,
      timezone: nextTimezone,
      rules: nextRules,
      exceptions: Array.isArray(nextAvailability.exceptions) ? nextAvailability.exceptions : []
    });
    setProviders(Array.isArray(providersJson.providers) ? providersJson.providers : []);
  }, [defaultHostTimezone, isEnglishMode]);

  const fetchDashboardData = useCallback(async () => {
    if (!selectedClientId) return;
    setIsLoading(true);
    setError("");

    try {
      const authHeaders = getAuthHeader();
      const [linksRes, appointmentsRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/booking/links?client_id=${encodeURIComponent(selectedClientId)}&limit=50`, { headers: authHeaders }),
        fetch(`${API_URL}/api/booking/appointments?client_id=${encodeURIComponent(selectedClientId)}&limit=100`, { headers: authHeaders }),
        fetch(`${API_URL}/api/booking/hosts?client_id=${encodeURIComponent(selectedClientId)}`, { headers: authHeaders })
      ]);

      const linksJson = await linksRes.json();
      const appointmentsJson = await appointmentsRes.json();
      const usersJson = await usersRes.json();

      if (!linksRes.ok) throw new Error(linksJson.error || "予約リンク一覧の取得に失敗しました。");
      if (!appointmentsRes.ok) throw new Error(appointmentsJson.error || "予約一覧の取得に失敗しました。");
      if (!usersRes.ok) throw new Error(usersJson.error || "担当ユーザー一覧の取得に失敗しました。");

      const hostRows = Array.isArray(usersJson.hosts)
        ? usersJson.hosts
        : Array.isArray(usersJson.data)
          ? usersJson.data
          : [];
      const nextUsers = hostRows.map((host) => ({
        ...host,
        name: host.name || host.display_name || host.email || `User #${host.id}`
      }));
      const nextBookingLinks = Array.isArray(linksJson.data) ? linksJson.data : [];
      const nextAppointments = Array.isArray(appointmentsJson.data) ? appointmentsJson.data : [];
      setBookingLinks(nextBookingLinks);
      setAppointments(nextAppointments);
      setUsers(nextUsers);
      hydrateOwnerState(nextUsers);
      return { success: true, bookingLinks: nextBookingLinks, appointments: nextAppointments };
    } catch (fetchError) {
      console.error("Booking dashboard fetch failed:", fetchError);
      setError(fetchError.message || "日程調整データの取得に失敗しました。");
      return { success: false, error: fetchError };
    } finally {
      setIsLoading(false);
    }
  }, [hydrateOwnerState, selectedClientId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (!selectedClientId || !selectedOwnerUserId) return;
    fetchUserScopedSettings(selectedClientId, selectedOwnerUserId).catch((fetchError) => {
      setError(fetchError.message || "担当ユーザー設定の取得に失敗しました。");
    });
  }, [fetchUserScopedSettings, selectedClientId, selectedOwnerUserId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oauthStatus = searchParams.get("booking_oauth_status");
    const oauthProvider = searchParams.get("booking_oauth_provider");
    const oauthMessage = searchParams.get("booking_oauth_message");

    if (oauthStatus && oauthMessage) {
      setNotice(oauthStatus === "success" ? `${oauthProvider || "provider"}: ${oauthMessage}` : "");
      setError(oauthStatus === "success" ? "" : `${oauthProvider || "provider"}: ${oauthMessage}`);
    }
  }, [location.search]);

  useEffect(() => {
    const fragment = new URLSearchParams(String(location.hash || "").replace(/^#/, ""));
    const completionToken = fragment.get("booking_oauth_completion");
    const providerId = fragment.get("booking_oauth_provider");
    const hasOauthFragment = fragment.has("booking_oauth_completion") || fragment.has("booking_oauth_provider");
    if (!hasOauthFragment || oauthCompletionCapturedHashRef.current === location.hash) return;

    // Fragments are not sent to the server or access logs. Remove the opaque
    // one-time completion token immediately, even while the authenticated user
    // and selected client are still hydrating. Keep it only in component memory.
    oauthCompletionCapturedHashRef.current = location.hash;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    if (!completionToken || !providerId
      || !/^[A-Za-z0-9_-]{43}$/.test(completionToken)
      || !["google_calendar", "google_meet", "zoom"].includes(providerId)) {
      setError(isEnglishMode ? "OAuth completion is invalid." : "OAuth連携情報を確認できませんでした。");
      setOauthCompletionPending(null);
      return;
    }
    setOauthCompletionPending({ completionToken, providerId });
  }, [isEnglishMode, location.hash]);

  useEffect(() => {
    if (!oauthCompletionPending || oauthCompletionInFlightRef.current || !selectedClientId || !user?.id) return;
    const { completionToken, providerId } = oauthCompletionPending;
    oauthCompletionInFlightRef.current = true;
    setOauthCompletionPending(null);
    setProviderLoadingId(providerId);
    setNotice("");
    setError("");

    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/booking/providers/${encodeURIComponent(providerId)}/complete`, {
          method: "POST",
          headers: getAuthHeader(true),
          body: JSON.stringify({
            client_id: selectedClientId,
            completion_token: completionToken
          })
        });
        const json = await response.json();
        if (!response.ok || json.success === false) {
          throw new Error(json.error || json.message || (isEnglishMode ? "OAuth connection could not be saved." : "OAuth連携を保存できませんでした。"));
        }
        const completedOwnerUserId = Number(json.owner_user_id) || Number(user.id);
        setSelectedOwnerUserId(String(completedOwnerUserId));
        setNotice(json.message || (isEnglishMode ? "OAuth connection saved." : "OAuth連携を保存しました。"));
        await Promise.all([
          fetchDashboardData(),
          fetchUserScopedSettings(selectedClientId, completedOwnerUserId)
        ]);
      } catch (completionError) {
        setError(completionError.message || (isEnglishMode ? "OAuth connection could not be saved." : "OAuth連携を保存できませんでした。もう一度お試しください。"));
      } finally {
        setProviderLoadingId("");
      }
    })();
  }, [
    fetchDashboardData,
    fetchUserScopedSettings,
    isEnglishMode,
    oauthCompletionPending,
    selectedClientId,
    user?.id
  ]);

  const linkRows = useMemo(() => (
    bookingLinks.map((link) => {
      const linkedAppointments = appointmentsByLinkId.get(Number(link.id)) || [];
      return {
        ...link,
        linkedAppointments,
        ownerLabel: userNameMap.get(Number(link.owner_user_id)) || `User #${link.owner_user_id || "?"}`,
        meetingPreferenceLabel: meetingPreferenceOptions.find((option) => option.value === (link.meeting_app_preference || "auto"))?.label || (isEnglishMode ? "Auto" : "自動判定")
      };
    })
  ), [appointmentsByLinkId, bookingLinks, isEnglishMode, meetingPreferenceOptions, userNameMap]);

  const handleDraftChange = (field, value) => {
    setCreateDraft((current) => ({ ...current, [field]: value }));
  };

  const normalizeDraftNumber = (value, fallback, min, max) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isInteger(parsed)) return fallback;
    return Math.min(Math.max(parsed, min), max);
  };

  const buildLinkPayload = () => ({
    client_id: selectedClientId,
    owner_user_id: Number(createDraft.owner_user_id),
    title: createDraft.title,
    description: createDraft.description,
    timezone: availability.timezone || defaultHostTimezone,
    meeting_app_preference: createDraft.meeting_app_preference,
    booking_notice_hours: normalizeDraftNumber(createDraft.booking_notice_hours, 1, 0, 720),
    max_days_in_future: normalizeDraftNumber(createDraft.max_days_in_future, 30, 1, 365)
  });

  const openNewLinkPanel = () => {
    setCreateDraft({
      title: isEnglishMode ? "Introductory meeting" : "初回相談",
      description: "",
      owner_user_id: selectedOwnerUserId || users[0]?.id || "",
      meeting_app_preference: "auto",
      booking_notice_hours: 1,
      max_days_in_future: 30
    });
    setSelectedPanel({
      kind: "new-link",
      title: copy.newLinkPanelTitle,
      subtitle: copy.newLinkPanelSubtitle
    });
  };

  const openEditLinkPanel = (link) => {
    setCreateDraft({
      title: link.title || "",
      description: link.description || "",
      owner_user_id: String(link.owner_user_id || selectedOwnerUserId || ""),
      meeting_app_preference: link.meeting_app_preference || "auto",
      booking_notice_hours: normalizeDraftNumber(link.booking_notice_hours, 1, 0, 720),
      max_days_in_future: normalizeDraftNumber(link.max_days_in_future, 30, 1, 365)
    });
    setSelectedPanel({
      kind: "edit-link",
      ...link,
      title: link.title || copy.untitledLink,
      subtitle: copy.editLinkPanelSubtitle
    });
  };

  const handleCreateLink = async () => {
    if (!selectedClientId || !createDraft.owner_user_id) return;
    setIsCreatingLink(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/booking/links`, {
        method: "POST",
        headers: getAuthHeader(true),
        body: JSON.stringify({
          ...buildLinkPayload(),
          source_type: "manual"
        })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "予約リンクの作成に失敗しました。");
      setNotice(isEnglishMode ? "Booking link created." : "予約リンクを作成しました。");
      await fetchDashboardData();
      setSelectedPanel(null);
    } catch (createError) {
      setError(createError.message || (isEnglishMode ? "Failed to create booking link." : "予約リンクの作成に失敗しました。"));
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleSaveLink = async () => {
    if (!selectedClientId || !selectedPanel?.id || !createDraft.owner_user_id) return;
    setIsSavingLink(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/booking/links/${encodeURIComponent(selectedPanel.id)}`, {
        method: "PATCH",
        headers: getAuthHeader(true),
        body: JSON.stringify(buildLinkPayload())
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || (isEnglishMode ? "Failed to save booking link." : "予約リンクの保存に失敗しました。"));
      }
      setNotice(isEnglishMode ? "Booking link saved." : "予約リンクを保存しました。");
      await fetchDashboardData();
      setSelectedPanel(null);
    } catch (saveError) {
      setError(saveError.message || (isEnglishMode ? "Failed to save booking link." : "予約リンクの保存に失敗しました。"));
    } finally {
      setIsSavingLink(false);
    }
  };

  const handleLinkStatusToggle = async (link) => {
    if (!selectedClientId || !link?.id) return;

      const nextAction = link.status === "active" ? "disable" : "publish";
    const nextLabel = nextAction === "publish" ? (isEnglishMode ? "publish" : "公開") : (isEnglishMode ? "unpublish" : "非公開");

    setLinkStatusLoadingId(link.id);
    setError("");
    setNotice("");

    try {
      const connectionWarning = getMeetingConnectionWarning(
        link.meeting_app_preference || "auto",
        providerConnections,
        copy
      );
      if (
        nextAction === "publish"
        && Number(link.owner_user_id) === Number(selectedOwnerUserId)
        && connectionWarning
      ) {
        setNotice(connectionWarning);
      }
      const res = await fetch(`${API_URL}/api/booking/links/${encodeURIComponent(link.id)}/${nextAction}`, {
        method: "POST",
        headers: getAuthHeader(true),
        body: JSON.stringify({
          client_id: selectedClientId
        })
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || (isEnglishMode ? `Failed to ${nextLabel} booking link.` : `予約リンクの${nextLabel}に失敗しました。`));
      }
      setNotice(isEnglishMode ? `Booking link ${nextLabel}ed.` : `予約リンクを${nextLabel}しました。`);
      await fetchDashboardData();
    } catch (statusError) {
      setError(statusError.message || (isEnglishMode ? `Failed to ${nextLabel} booking link.` : `予約リンクの${nextLabel}に失敗しました。`));
    } finally {
      setLinkStatusLoadingId(null);
    }
  };

  const handleCancelAppointment = async (appointment) => {
    if (!selectedClientId || !appointment?.id) return;
    if (appointmentResyncInFlightRef.current.has(String(appointment.id))) return;

    const shouldCancel = window.confirm(isEnglishMode
      ? "Cancel this booking and make the time slot available again?"
      : "この予約をキャンセルして、対象の時間枠を再度予約可能にします。よろしいですか？");
    if (!shouldCancel) return;

    setAppointmentActionLoadingId(appointment.id);
    setError("");
    setNotice("");

    try {
      const res = await fetch(`${API_URL}/api/booking/appointments/${appointment.id}/cancel`, {
        method: "POST",
        headers: {
          ...getAuthHeader(true)
        },
        body: JSON.stringify({
          client_id: selectedClientId
        })
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || (isEnglishMode ? "Failed to cancel the booking." : "予約のキャンセルに失敗しました。"));
      }

      setAppointments((current) => current.map((item) => (
        String(item.id) === String(appointment.id)
          ? { ...item, ...json.appointment }
          : item
      )));
      setSelectedAppointment(json.appointment || null);
      setNotice(isEnglishMode ? "Booking cancelled and the time slot was released." : "予約をキャンセルし、時間枠を解放しました。");
    } catch (fetchError) {
      setError(fetchError.message || (isEnglishMode ? "Failed to cancel the booking." : "予約のキャンセルに失敗しました。"));
    } finally {
      setAppointmentActionLoadingId(null);
    }
  };

  const handleRetryProviderCleanup = async (appointment) => {
    if (!selectedClientId || !appointment?.id || !appointment.provider_cleanup_required) return;
    if (String(appointmentActionLoadingId) === String(appointment.id)) return;
    setAppointmentActionLoadingId(appointment.id);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${API_URL}/api/booking/appointments/${encodeURIComponent(appointment.id)}/provider-cleanup`, {
        method: "POST",
        headers: getAuthHeader(true),
        body: JSON.stringify({ client_id: selectedClientId })
      });
      const json = await res.json();
      if (json.appointment) {
        setAppointments((current) => current.map((item) => (
          String(item.id) === String(appointment.id) ? { ...item, ...json.appointment } : item
        )));
        setSelectedAppointment((current) => (
          String(current?.id) === String(appointment.id) ? { ...current, ...json.appointment } : current
        ));
      }
      if (!res.ok || json.success === false) {
        throw new Error(json.warning || json.error || copy.providerCleanupHelp);
      }
      setNotice(copy.providerCleanupSuccess);
    } catch (cleanupError) {
      setError(cleanupError.message || copy.providerCleanupHelp);
    } finally {
      setAppointmentActionLoadingId(null);
    }
  };

  const handleResyncAppointment = async (appointment) => {
    if (!selectedClientId || !appointment?.id || !canResyncAppointment(appointment)) return;
    if (String(appointmentActionLoadingId) === String(appointment.id)) return;

    const appointmentKey = String(appointment.id);
    if (appointmentResyncInFlightRef.current.has(appointmentKey)) return;
    appointmentResyncInFlightRef.current.add(appointmentKey);
    setAppointmentResyncLoadingId(appointment.id);
    setAppointmentResyncFeedback({ appointmentId: appointment.id, type: "", message: "" });
    setError("");
    setNotice("");

    try {
      const res = await fetch(`${API_URL}/api/booking/appointments/${encodeURIComponent(appointment.id)}/resync`, {
        method: "POST",
        headers: getAuthHeader(true),
        body: JSON.stringify({ client_id: selectedClientId })
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || (isEnglishMode ? "Failed to retry the meeting URL sync." : "会議URLの再発行に失敗しました。"));
      }

      const responseAppointment = {
        ...(json.appointment || appointment),
        ...(json.reconciliation_required === true ? { provider_reconciliation_required: true } : {}),
      };
      setAppointments((current) => current.map((item) => (
        String(item.id) === appointmentKey ? { ...item, ...responseAppointment } : item
      )));
      setSelectedAppointment((current) => (
        String(current?.id) === appointmentKey ? { ...current, ...responseAppointment } : current
      ));

      const refreshed = await fetchDashboardData();
      if (!refreshed?.success) {
        throw new Error(isEnglishMode
          ? "The sync completed, but the refreshed booking state could not be loaded. Please reopen the booking."
          : "再同期後の予約状態を再取得できませんでした。予約を開き直してご確認ください。");
      }

      const refreshedAppointment = {
        ...(refreshed.appointments.find((item) => String(item.id) === appointmentKey) || responseAppointment),
        ...(json.reconciliation_required === true ? { provider_reconciliation_required: true } : {}),
      };
      setSelectedAppointment((current) => (
        String(current?.id) === appointmentKey ? refreshedAppointment : current
      ));

      if (json.reconciliation_required === true) {
        setAppointmentResyncFeedback({
          appointmentId: appointment.id,
          type: "error",
          message: copy.resyncMeetingReconciliation
        });
        return;
      }

      if (json.booking_pending === true || canResyncAppointment(refreshedAppointment)) {
        setAppointmentResyncFeedback({
          appointmentId: appointment.id,
          type: "error",
          message: json.warning || copy.resyncMeetingPending
        });
        return;
      }

      if (String(refreshedAppointment?.status || "").toLowerCase() !== "confirmed") {
        setAppointmentResyncFeedback({
          appointmentId: appointment.id,
          type: "error",
          message: isEnglishMode
            ? "The booking status changed during the retry. Review the current status before taking another action."
            : "再試行中に予約状態が変わりました。現在の状態を確認してから次の操作を行ってください。"
        });
        return;
      }

      setAppointmentResyncFeedback({
        appointmentId: appointment.id,
        type: "success",
        message: copy.resyncMeetingSuccess
      });
      setNotice(copy.resyncMeetingSuccess);
    } catch (resyncError) {
      setAppointmentResyncFeedback({
        appointmentId: appointment.id,
        type: "error",
        message: resyncError.message || (isEnglishMode ? "Failed to retry the meeting URL sync." : "会議URLの再発行に失敗しました。")
      });
    } finally {
      appointmentResyncInFlightRef.current.delete(appointmentKey);
      setAppointmentResyncLoadingId(null);
    }
  };

  const handleRuleChange = (index, field, value) => {
    setAvailabilityFeedback({ type: "", message: "" });
    setAvailability((current) => {
      const nextRules = [...(current.rules || [])];
      nextRules[index] = { ...nextRules[index], [field]: value };
      return { ...current, rules: nextRules };
    });
  };

  const handleAvailabilityTimezoneChange = (timezone) => {
    const nextTimezone = normalizeTimezone(timezone, defaultHostTimezone);
    setAvailabilityFeedback({ type: "", message: "" });
    setAvailability((current) => ({
      ...current,
      timezone: nextTimezone,
      rules: (current.rules || []).map((rule) => ({
        ...rule,
        timezone: nextTimezone
      }))
    }));
  };

  const handleAddRule = () => {
    setAvailabilityFeedback({ type: "", message: "" });
    setAvailability((current) => ({
      ...current,
      timezone: current.timezone || defaultHostTimezone,
      rules: [
        ...(current.rules || []),
        {
          day_of_week: 1,
          start_time: "10:00:00",
          end_time: "18:00:00",
          timezone: current.timezone || defaultHostTimezone,
          slot_duration_minutes: 30,
          status: "active"
        }
      ]
    }));
  };

  const handleRemoveRule = (index) => {
    setAvailabilityFeedback({ type: "", message: "" });
    setAvailability((current) => ({
      ...current,
      rules: (current.rules || []).filter((_, ruleIndex) => ruleIndex !== index)
    }));
  };

  const handleSaveAvailability = async () => {
    if (!selectedClientId || !selectedOwnerUserId) return;
    setIsSavingAvailability(true);
    setError("");
    setAvailabilityFeedback({ type: "", message: "" });
    const timezone = availability.timezone || defaultHostTimezone;

    try {
      const res = await fetch(`${API_URL}/api/booking/availability`, {
        method: "PUT",
        headers: getAuthHeader(true),
        body: JSON.stringify({
          client_id: selectedClientId,
          owner_user_id: Number(selectedOwnerUserId),
          timezone,
          rules: (availability.rules || []).map((rule) => ({
            ...rule,
            timezone
          }))
        })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "空き枠設定の保存に失敗しました。");
      setAvailability(json.availability || { timezone, rules: [], exceptions: [] });
      const successMessage = isEnglishMode ? "Availability saved." : "空き枠設定を保存しました。";
      setAvailabilityFeedback({ type: "success", message: successMessage });
      setNotice(successMessage);
    } catch (saveError) {
      const errorMessage = saveError.message || (isEnglishMode ? "Failed to save availability." : "空き枠設定の保存に失敗しました。");
      setAvailabilityFeedback({ type: "error", message: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const handleProviderAction = async (providerId, action) => {
    if (!selectedClientId || !selectedOwnerUserId) return;
    if (!canManageProviderConnection(providerId)) {
      setNotice("");
      setError(copy.providerOwnerOnly);
      return;
    }
    setProviderLoadingId(providerId);
    setNotice("");
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/booking/providers/${providerId}/${action}`, {
        method: "POST",
        headers: getAuthHeader(true),
        body: JSON.stringify({
          client_id: selectedClientId,
          owner_user_id: Number(selectedOwnerUserId),
          external_account_email: user?.email || ""
        })
      });
      const json = await res.json();
      if (!res.ok || json.success === false) throw new Error(json.error || "連携設定の更新に失敗しました。");
      if (json.auth_url) {
        window.location.assign(json.auth_url);
        return;
      }
      await fetchUserScopedSettings(selectedClientId, selectedOwnerUserId);
    } catch (providerError) {
      setError(providerError.message || "連携設定の更新に失敗しました。");
    } finally {
      setProviderLoadingId("");
    }
  };

  const handleUserSettingsTarget = (value) => {
    if (value === "__close__") {
      setIsUserSettingsOpen(false);
      return;
    }
    setSelectedOwnerUserId(value);
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 sm:px-6 py-6 sm:py-7 text-white shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-blue-200">Booking Workspace</p>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold">{copy.workspaceTitle}</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-200">
              {copy.workspaceDescription}
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:grid-cols-2 lg:flex lg:flex-wrap">
            <button
              type="button"
              onClick={() => setIsUserSettingsOpen(true)}
              className="rounded-full border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/15"
            >
              {copy.userSettings}
            </button>
            <button
              type="button"
              onClick={openNewLinkPanel}
              className="rounded-full bg-white px-5 py-2.5 text-sm font-bold text-slate-900 transition hover:bg-slate-100"
            >
              {copy.newLinkButton}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { id: "links", label: copy.activeLinks, value: String(activeLinkCount), tone: "bg-blue-50 text-blue-700 border-blue-100" },
          { id: "appointments", label: copy.weekAppointments, value: String(thisWeekAppointments), tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
          { id: "connections", label: copy.activeConnections, value: String(activeConnectionCount), tone: "bg-amber-50 text-amber-700 border-amber-100" }
        ].map((card) => (
          <div key={card.id} className={`rounded-3xl border p-5 shadow-sm ${card.tone}`}>
            <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80">{card.label}</p>
            <p className="mt-3 text-3xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {notice ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div> : null}

      <SectionCard
        title={copy.linkListTitle}
        description={copy.linkListDescription}
      >
        <div className="space-y-4">
          {linkRows.map((link) => {
            const isOpen = openLogLinkId === link.id;
            const connectionWarning = Number(link.owner_user_id) === Number(selectedOwnerUserId)
              ? getMeetingConnectionWarning(link.meeting_app_preference || "auto", providerConnections, copy)
              : "";

            return (
              <div key={link.id} className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                <div className="grid gap-4 px-5 py-5 lg:grid-cols-[1.2fr_0.9fr_0.8fr_auto] lg:items-center">
                  <button
                    type="button"
                    onClick={() => openEditLinkPanel(link)}
                    className="min-w-0 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="min-w-0 break-words text-lg font-bold text-gray-900">{link.title || copy.untitledLink}</h3>
                      <StatusPill value={link.status} />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {link.deal_id ? `${isEnglishMode ? "Deal" : "案件"} #${link.deal_id}` : link.marketing_lead_id ? `${isEnglishMode ? "Lead" : "リード"} #${link.marketing_lead_id}` : copy.createdManually} / {copy.ownerLabel}: {link.ownerLabel}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-gray-400">
                      {copy.minimumNoticeHours}: {Number(link.booking_notice_hours ?? 1)}{copy.hoursUnit} / {copy.maxDaysInFuture}: {Number(link.max_days_in_future ?? 30)}{copy.daysUnit}
                    </p>
                  </button>

                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.meetingUrl}</p>
                    <p className="mt-2 text-sm font-bold text-gray-900">{link.meetingPreferenceLabel}</p>
                    {connectionWarning ? (
                      <p className="mt-2 text-xs font-semibold leading-5 text-amber-700">
                        {connectionWarning}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">{copy.publicUrl}</p>
                    {link.status === "active" ? (
                      <a href={buildPublicBookingUrl(link.public_id)} target="_blank" rel="noreferrer" className="mt-2 block truncate text-sm font-bold text-blue-600 hover:text-blue-700">
                        {copy.openBookingPage}
                      </a>
                    ) : (
                      <p className="mt-2 text-sm font-bold text-amber-600">
                        {copy.draftMessage}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                    <button
                      type="button"
                      onClick={() => handleLinkStatusToggle(link)}
                      disabled={linkStatusLoadingId === link.id}
                      className={`rounded-full px-4 py-2 text-sm font-bold transition disabled:opacity-60 ${
                        link.status === "active"
                          ? "border border-amber-300 text-amber-700 hover:bg-amber-50"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      {linkStatusLoadingId === link.id
                        ? copy.publishing
                        : link.status === "active"
                          ? copy.unpublish
                          : copy.publish}
                    </button>

                    <button
                      type="button"
                      onClick={() => setOpenLogLinkId(isOpen ? null : link.id)}
                      className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                        isOpen
                          ? "bg-gray-900 text-white hover:bg-gray-800"
                          : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {isOpen ? copy.closeLog : copy.viewLog(link.linkedAppointments.length)}
                    </button>
                  </div>
                </div>

                {isOpen ? (
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    {link.linkedAppointments.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-4 text-sm text-gray-500">
                        {copy.noAppointments}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {link.linkedAppointments.map((appointment) => {
                          const isCancellingThisAppointment = appointmentActionLoadingId === appointment.id;
                          const isResyncingThisAppointment = appointmentResyncLoadingId === appointment.id;
                          const isCancellable = canCancelAppointment(appointment);
                          const riskScore = getRiskScore(appointment);

                          return (
                            <div
                              key={appointment.id}
                              className="grid w-full grid-cols-1 gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left transition hover:border-emerald-200 hover:bg-emerald-50/50 sm:grid-cols-[1.2fr_1fr_auto] sm:items-center"
                            >
                              <button
                                type="button"
                                onClick={() => setSelectedAppointment(appointment)}
                                className="min-w-0 text-left"
                              >
                                <p className="text-sm font-bold text-gray-900">{appointment.invitee_name}</p>
                                <p className="mt-1 text-sm text-gray-500">{appointment.invitee_company || copy.companyUnset}</p>
                                {riskScore > 0 ? (
                                  <span className={`mt-2 inline-flex w-fit rounded-full border px-2.5 py-1 text-[11px] font-black ${getRiskTone(appointment)}`}>
                                    {copy.riskScore}: {riskScore}
                                  </span>
                                ) : null}
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedAppointment(appointment)}
                                className="text-left text-sm text-gray-600"
                              >
                                {formatDateTime(appointment.started_at)}
                              </button>
                              <div className="flex flex-col gap-2 sm:items-end">
                                <StatusPill value={appointment.status} />
                                {isCancellable ? (
                                  <button
                                    type="button"
                                    onClick={() => handleCancelAppointment(appointment)}
                                    disabled={isCancellingThisAppointment || isResyncingThisAppointment}
                                    className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                                  >
                                    {isCancellingThisAppointment ? copy.releasing : copy.releaseSlot}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}

          {!isLoading && linkRows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              {copy.noLinks}
            </div>
          ) : null}
        </div>
      </SectionCard>

      <BookingLinkDrawer
        panel={selectedPanel}
        users={users}
        createDraft={createDraft}
        connectionWarning={createDraftConnectionWarning}
        meetingPreferenceOptions={meetingPreferenceOptions}
        copy={copy}
        isCreatingLink={isCreatingLink}
        isSavingLink={isSavingLink}
        onDraftChange={handleDraftChange}
        onCreateLink={handleCreateLink}
        onSaveLink={handleSaveLink}
        onClose={() => setSelectedPanel(null)}
      />

      <AppointmentDetailDrawer
        appointment={selectedAppointment}
        onCancelAppointment={handleCancelAppointment}
        onResyncAppointment={handleResyncAppointment}
        onRetryProviderCleanup={handleRetryProviderCleanup}
        isCancellingAppointment={appointmentActionLoadingId === selectedAppointment?.id}
        isResyncingAppointment={appointmentResyncLoadingId === selectedAppointment?.id}
        resyncFeedback={String(appointmentResyncFeedback.appointmentId) === String(selectedAppointment?.id)
          ? appointmentResyncFeedback
          : null}
        onClose={() => setSelectedAppointment(null)}
        copy={copy}
      />

      <UserSettingsModal
        open={isUserSettingsOpen}
        users={users}
        selectedOwnerUserId={selectedOwnerUserId}
        onOwnerChange={handleUserSettingsTarget}
        providers={providers}
        providerCards={providerCards}
        providerLoadingId={providerLoadingId}
        onProviderAction={handleProviderAction}
        canManageProviderConnection={canManageProviderConnection}
        availability={availability}
        onTimezoneChange={handleAvailabilityTimezoneChange}
        onRuleChange={handleRuleChange}
        onAddRule={handleAddRule}
        onRemoveRule={handleRemoveRule}
        onSaveAvailability={handleSaveAvailability}
        isSavingAvailability={isSavingAvailability}
        availabilityFeedback={availabilityFeedback}
        isEnglishMode={isEnglishMode}
        dayLabels={dayLabels}
        copy={copy}
      />

      {isCreatingLink ? (
        <div className="fixed bottom-4 left-4 right-4 rounded-full bg-gray-900 px-4 py-2 text-center text-sm font-bold text-white shadow-lg sm:left-auto sm:right-6 sm:bottom-6">
          {copy.creatingLink}
        </div>
      ) : null}
    </div>
  );
}
